
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/contexts/UserContext';
import { profileConsolidatedService } from '@/services/profile/profileConsolidatedService';
import { CONSOLIDATED_PROFILE, getEntityKey } from '@/constants/queryKeys';
import { toast } from 'sonner';

/**
 * Consolidated hook that fetches all profile-related data in a single optimized query
 * This replaces multiple separate hooks with one efficient API call
 * Note: This hook only fetches existing profiles, it does not create them.
 * Profile creation happens during registration via database trigger.
 */
export const useConsolidatedProfile = () => {
  const { user, isLoading: authLoading } = useUser();
  
  const {
    data: consolidatedData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: getEntityKey(CONSOLIDATED_PROFILE, user?.id),
    queryFn: async () => {
      if (!user?.id) {
        console.log('No user ID available for consolidated profile fetch');
        return null;
      }
      
      console.log('Attempting to fetch consolidated profile data for user:', user.id);
      
      try {
        // Fetch consolidated data - do not create if missing
        const consolidatedData = await profileConsolidatedService.getConsolidatedProfileData(user.id);
        
        if (!consolidatedData) {
          console.log('No consolidated profile data found for user:', user.id);
          return null;
        }
        
        console.log('Consolidated profile data found:', consolidatedData);
        return consolidatedData;
      } catch (err) {
        console.error('Error in useConsolidatedProfile queryFn:', err);
        throw err;
      }
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry twice on failure
    meta: {
      onError: (error) => {
        console.error('Query error in useConsolidatedProfile:', error);
        toast.error('Error loading profile. Please try again.');
      }
    }
  });

  // Transform the consolidated data into the format expected by existing components
  const transformedData = consolidatedData ? {
    // Profile data
    profile: {
      id: consolidatedData.profile_id,
      first_name: consolidatedData.first_name,
      last_name: consolidatedData.last_name,
      family_role: consolidatedData.family_role,
      registration_family_id: consolidatedData.registration_family_id,
      current_family_id: consolidatedData.current_family_id,
      invite_code: consolidatedData.invite_code,
      created_at: consolidatedData.created_at,
      updated_at: consolidatedData.updated_at,
    },
    
    // Family members (excluding current user)
    familyMembers: consolidatedData.family_members.filter(
      member => member.id !== user?.id
    ),
    
    // Invite code
    inviteCode: consolidatedData.invite_code,
    
    // Family admin data
    isFamilyAdmin: consolidatedData.is_family_admin,
    familyAdmin: consolidatedData.family_admin_id ? {
      id: consolidatedData.family_admin_id,
      first_name: consolidatedData.family_admin_first_name,
      last_name: consolidatedData.family_admin_last_name,
    } : null,
    
    // Family management data
    isSingleMemberFamily: consolidatedData.is_single_member_family,
    canSwitchFamily: consolidatedData.can_switch_family,
    switchFamilyWarning: consolidatedData.switch_family_warning,
  } : null;

  return {
    ...transformedData,
    isLoading: isLoading || authLoading,
    error: error ? String(error) : null,
    refetchProfile: refetch
  };
};
